import { ThunkAction } from 'redux-thunk';

import moment from 'moment-timezone';

import UnauthenticatedUserSessionModel from 'gen-thrift/unauthenticated_user_session_Model_types';

import { ILocationService } from 'api/Location/interfaces/ILocationService';
import { AccountType } from 'api/Location/model/AccountType';
import { LocationId } from 'api/Location/model/LocationId';

import { UserAccountId } from 'api/UserAccount/model/UserAccountId';

import { UnknownCookieException } from 'shared/lib/cookie/exceptions/UnknownCookieException';
import { ICookieReader } from 'shared/lib/cookie/interfaces/ICookieReader';
import { UnauthenticatedUserSessionManagerImpl } from 'shared/lib/unauthenticated_user_session/impl/UnauthenticatedUserSessionManagerImpl';

import { RuntimeException } from 'shared/lib/general/exceptions/RuntimeException';
import { ISignUpAndLoginButtonsState } from '../SignUpAndLoginButtons/reducers';

import { IActionCreatorsMapObject } from 'shared/models/IAction';

import { RetailerGettingStartedType } from 'api/Location/model/RetailerGettingStartedType';
import { AddressFormFieldName } from 'shared/components/AddressForm/AddressForm';
import { IValidationInputData } from 'shared/components/ValidationInput';
import { Observer } from 'shared/utils/observer';
import { PathUtils } from 'shared/utils/pathUtils';

interface IPublicSiteStore {
    signUpAndLoginButtonsState : ISignUpAndLoginButtonsState;
}

export namespace RetailerOnrampFormActionInterfaces {
    export interface IRetailerOnrampFormActionCreatorsMapObject extends IActionCreatorsMapObject {
        onCreateRetailer : (
            userAccountIdentifier : UserAccountId,
            userIsNewAccount : boolean,
            retailerName : string,
            addressValidationInputDataByFieldName : {[fieldName in AddressFormFieldName] : IValidationInputData },
            userFirstName : string,
            userLastName : string,
            userPhone : string,
            userEmail : string,
            retailerAccountType : AccountType,
            retailerOnrampType : RetailerGettingStartedType | null,
        ) => ThunkAction<Promise<void>, IPublicSiteStore, {services : RetailerOnrampFormActionInterfaces.IServices}>;
    }

    export interface IServices {
        cookieReader : ICookieReader;
        // publicContentViewEventService : IPublicContentViewEventService;
        unauthenticatedUserSessionManager : UnauthenticatedUserSessionManagerImpl;
        locationService : ILocationService;
    }
}

const onCreateRetailer = (
    userAccountId : UserAccountId,
    userIsNewAccount : boolean,
    retailerName : string,
    addressValidationInputDataByFieldName : {[fieldName in AddressFormFieldName] : IValidationInputData },
    userFirstName : string,
    userLastName : string,
    userPhone : string,
    userEmail : string,
    retailerAccountType : AccountType,
    retailerOnrampType : RetailerGettingStartedType | null,
) : ThunkAction<Promise<void>, IPublicSiteStore, {services : RetailerOnrampFormActionInterfaces.IServices}> => {
    return (dispatch, getState, extraArguments) : Promise<void> => {
        const timezone = moment.tz.guess();

        // const retailerName = inputDataByFieldName.barName.value;

        return extraArguments.services.locationService.createLocations(
            userFirstName,
            userLastName,
            userAccountId.getValue(),
            userEmail,
            userPhone,
            retailerAccountType === AccountType.BEVERAGE ? retailerName : null,
            retailerAccountType === AccountType.FOOD ? retailerName : null,
            addressValidationInputDataByFieldName.streetAddressLine1.value,
            addressValidationInputDataByFieldName.streetAddressLine2.value,
            addressValidationInputDataByFieldName.city.value,
            addressValidationInputDataByFieldName.state.value,
            addressValidationInputDataByFieldName.country.value,
            addressValidationInputDataByFieldName.zipCode.value,
            timezone,
            true,
            {
                freeTrialSource: window.location.href,
            },
            retailerOnrampType,
        )
        .then((result) => {
            const beverageLocationId = result.beverageLocationId;
            const foodLocationId = result.foodLocationId;

            let retailerIdForRedirect : LocationId | null;
            if (retailerAccountType === AccountType.BEVERAGE) {
                retailerIdForRedirect = beverageLocationId;
            } else {
                retailerIdForRedirect = foodLocationId;
            }

            let hubspotCookie : string | null = null;
            try {
                hubspotCookie = extraArguments.services.cookieReader.getCookie('hubspotutk');
            } catch (error) {
                if (!(error instanceof UnknownCookieException)) {
                    throw error;
                }
            }

            let userCreatedConversionPromise : Promise<void>;
            if (userIsNewAccount) {
                userCreatedConversionPromise = Observer.trackConversion.userCreated({
                    email: userEmail,
                    phone: userPhone,
                    bar_name: retailerName,
                    first_name: userFirstName,
                    last_name: userLastName,
                    zip_code: addressValidationInputDataByFieldName.zipCode.value,
                    subscribe_to_blog: false, // always false now?
                    hubspot_cookie: hubspotCookie,
                    is_admin_created: false,
                    source_url: window.location.href,
                    should_receive_marketing: true,
                    creator_user_id: null,
                    retailer_id: null,
                });
            } else {
                userCreatedConversionPromise = Promise.resolve();
            }

            return Promise.all([
                extraArguments.services.unauthenticatedUserSessionManager.getOrCreateUnauthenticatedUserSessionId(),
                userCreatedConversionPromise,
                Observer.trackConversion.retailerCreated({
                    user_first_name: userFirstName,
                    user_last_name: userLastName,
                    user_email: userEmail,
                    user_phone : userPhone,
                    beverage_retailer_id: (beverageLocationId === null) ? null : beverageLocationId.getValue(),
                    food_retailer_id: (foodLocationId === null) ? null : foodLocationId.getValue(),
                    source_url: document.referrer,
                    retailer_name: retailerName,
                    retailer_address_line1: addressValidationInputDataByFieldName.streetAddressLine1.value,
                    retailer_address_line2: addressValidationInputDataByFieldName.streetAddressLine2.value,
                    retailer_country_code: addressValidationInputDataByFieldName.country.value,
                    retailer_city : addressValidationInputDataByFieldName.city.value,
                    retailer_zipcode: addressValidationInputDataByFieldName.zipCode.value,
                    group_location_count: null,
                    introduction_source: null,
                    introduction_drilldown: null,
                    introduction_free_text: null,
                    product_subscription: null, // TODO verify
                    hubspot_cookie: hubspotCookie,
                    is_admin_created: false,
                    should_receive_marketing: true,
                    creator_user_id: userAccountId.getValue(),
                    promo_code: null,
                    getting_started_source: window.location.href,
                    beverage_free_trial_getting_started_type: (beverageLocationId === null) ? null : retailerOnrampType,
                    food_free_trial_getting_started_type: (foodLocationId === null) ? null : retailerOnrampType,
                })
            ])
            .then((result1) => {
                const unauthenticatedUserSessionId : UnauthenticatedUserSessionModel.UnauthenticatedUserSessionId = result1[0];
                const userFullName = userFirstName + ' ' + userLastName;
                Observer.identifyAuthenticated(userAccountId, userFullName, userEmail, null);
                Observer.identifyUnauthenticated(unauthenticatedUserSessionId);

                if (userIsNewAccount) {
                    Observer.observeAction('New User Account Created', {
                        referrer: document.referrer,
                        page: window.location.pathname,
                        unauthenticatedUserSessionId: unauthenticatedUserSessionId.value,
                    });

                    Observer.observeAction('Clicked button to submit sign up form', {});
                    Observer.observeAction('Successfully Submitted Sign Up Form', {
                        userAccountId: userAccountId.getValue(),
                    });
                }

                if (retailerIdForRedirect === null) {
                    throw new RuntimeException('unexpected retailerIdForRedirect is null');
                }

                if (retailerOnrampType === 'inventory_template' || retailerOnrampType === 'invoice_upload' || retailerOnrampType === 'invoice_upload_food') {
                    window.location.href = PathUtils.getAbsolutePathForRequest(`/account/item_manager/r/${ retailerIdForRedirect.getValue() }`);
                } else if (retailerOnrampType === 'inventory_template_food') {
                    window.location.href = PathUtils.getAbsolutePathForRequest(`/account/onboarding_flow/quick_add/r/${ retailerIdForRedirect.getValue() }/&active_page=select-products`);
                } else if (retailerOnrampType === 'recipe_costing' || retailerOnrampType === 'recipe_costing_food' || retailerOnrampType === 'drink_price_tool') {
                    window.location.href = PathUtils.getAbsolutePathForRequest(`/sales/items/sales_item_manager/r/${ retailerIdForRedirect.getValue() }`);
                } else {
                    window.location.href = PathUtils.getAbsolutePathForRequest(`/account/dashboard/${ retailerIdForRedirect.getValue() }`);
                }
            });
        })
        .catch((error : Error) => {
            // dispatch(setIsSubmitting(false));
            throw error;
        });
    };
};

export const RetailerOnrampFormActions : RetailerOnrampFormActionInterfaces.IRetailerOnrampFormActionCreatorsMapObject = {
    onCreateRetailer,
};
